// frontend/src/pages/StaffAssignmentImport.jsx
// 配置履歴CSVインポート（メニューから直接アクセス可能なスタンドアロンページ）

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, RefreshCw, AlertCircle, CheckCircle, Download } from 'lucide-react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import { writeClientAudit } from '../utils/auditClient';
import Header from '../components/ui/Header';
import Papa from 'papaparse';
import dayjs from 'dayjs';

const StaffAssignmentImport = () => {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id;

  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({
    date: '', employee_id: '', role: '', procedure_name: '', department_name: '',
    pairing_mode: '', paired_with_employee_id: '',
    level_at_assignment: '', level_after_assignment: '', note: '',
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');
  const [allData, setAllData] = useState([]);

  // pairing_mode の自由表記 → 正規化
  const PAIRING_CSV_MAP = {
    '指導': 'instructor', '指導者': 'instructor', '指導者として': 'instructor',
    '被指導': 'trainee', '被指導者': 'trainee', '被指導者として': 'trainee', '育成対象': 'trainee',
    '独立': 'solo', '1人': 'solo', '一人': 'solo', 'ソロ': 'solo',
    'instructor': 'instructor', 'trainer': 'instructor', 'teacher': 'instructor',
    'trainee': 'trainee', 'student': 'trainee', 'learner': 'trainee',
    'solo': 'solo', 'alone': 'solo', 'single': 'solo',
  };
  const normalizePairingMode = (raw) => {
    if (!raw) return null;
    const k = String(raw).trim().toLowerCase();
    if (!k) return null;
    return PAIRING_CSV_MAP[k] || PAIRING_CSV_MAP[String(raw).trim()] || null;
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const h = res.meta.fields || [];
        setHeaders(h);
        setPreview(res.data.slice(0, 5));
        setAllData(res.data);
        // 自動マッピング
        const autoMap = {
          date: '', employee_id: '', role: '', procedure_name: '', department_name: '',
          pairing_mode: '', paired_with_employee_id: '',
          level_at_assignment: '', level_after_assignment: '', note: '',
        };
        h.forEach(col => {
          const c = col.toLowerCase();
          if (!autoMap.date && (c.includes('date') || c.includes('日付') || c.includes('配置日'))) autoMap.date = col;
          if (!autoMap.employee_id && (c.includes('employee') || c.includes('職員') || c === 'id' || c.includes('番号'))) autoMap.employee_id = col;
          if (!autoMap.role && (c.includes('role') || c.includes('役割') || c.includes('担当'))) autoMap.role = col;
          if (!autoMap.procedure_name && (c.includes('procedure') || c.includes('術式') || c.includes('手術'))) autoMap.procedure_name = col;
          if (!autoMap.department_name && (c.includes('department') || c.includes('診療科') || c.includes('科'))) autoMap.department_name = col;
          if (!autoMap.pairing_mode && (c.includes('pairing') || c.includes('指導役割') || c.includes('指導関係') || c.includes('教育'))) autoMap.pairing_mode = col;
          if (!autoMap.paired_with_employee_id && (c.includes('paired') || c.includes('ペア') || c.includes('指導者番号') || c.includes('被指導者番号'))) autoMap.paired_with_employee_id = col;
          if (!autoMap.level_at_assignment && (c.includes('level_at') || c.includes('配置時') || c.includes('スキル前') || c.includes('before'))) autoMap.level_at_assignment = col;
          if (!autoMap.level_after_assignment && (c.includes('level_after') || c.includes('配置後') || c.includes('スキル後') || c.includes('after'))) autoMap.level_after_assignment = col;
          if (!autoMap.note && (c.includes('note') || c.includes('メモ') || c.includes('備考'))) autoMap.note = col;
        });
        setMapping(autoMap);
      },
      error: () => alert('CSVの読み込みに失敗しました'),
    });
  };

  const handleImport = async () => {
    if (!mapping.date || !mapping.employee_id || !mapping.role) {
      alert('日付・職員番号・役割のマッピングは必須です');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      // staff取得（employee_id → staff.id変換用）
      const { data: staffData } = await supabase
        .from('staff')
        .select('id, employee_id, last_name, first_name')
        .eq('organization_id', organizationId);
      const empToId = {};
      const empToName = {};
      (staffData || []).forEach(s => {
        empToId[String(s.employee_id)] = s.id;
        empToName[String(s.employee_id)] = `${s.last_name || ''} ${s.first_name || ''}`.trim();
      });

      const rows = [];
      const errors = [];

      allData.forEach((row, i) => {
        const rawDate = row[mapping.date]?.trim();
        const rawEmpId = row[mapping.employee_id]?.trim();
        const rawRole = row[mapping.role]?.trim()?.toLowerCase();
        const procName = mapping.procedure_name ? row[mapping.procedure_name]?.trim() : '';
        const deptName = mapping.department_name ? row[mapping.department_name]?.trim() : '';
        const rawPairing = mapping.pairing_mode ? row[mapping.pairing_mode]?.trim() : '';
        const rawPairedEmp = mapping.paired_with_employee_id ? row[mapping.paired_with_employee_id]?.trim() : '';
        const lvlAt = mapping.level_at_assignment ? row[mapping.level_at_assignment]?.trim() : '';
        const lvlAfter = mapping.level_after_assignment ? row[mapping.level_after_assignment]?.trim() : '';
        const noteVal = mapping.note ? row[mapping.note]?.trim() : '';

        if (!rawDate || !rawEmpId || !rawRole) {
          errors.push(`行${i + 2}: 必須項目不足`);
          return;
        }

        const date = dayjs(rawDate);
        if (!date.isValid()) { errors.push(`行${i + 2}: 日付が無効 "${rawDate}"`); return; }

        // role正規化
        let role = '';
        if (rawRole.includes('scrub') || rawRole.includes('器械') || rawRole.includes('外科')) role = 'scrub';
        else if (rawRole.includes('circ') || rawRole.includes('外回') || rawRole.includes('外周')) role = 'circulating';
        else { errors.push(`行${i + 2}: 役割が不明 "${rawRole}"（scrub/circulating または器械出し/外回り）`); return; }

        const staffId = empToId[String(rawEmpId)];
        if (!staffId) { errors.push(`行${i + 2}: 職員番号 "${rawEmpId}" が見つかりません`); return; }

        const pairingMode = normalizePairingMode(rawPairing);
        const pairedStaffId = rawPairedEmp ? empToId[String(rawPairedEmp)] : null;
        const pairedName = rawPairedEmp ? empToName[String(rawPairedEmp)] : null;

        rows.push({
          organization_id: organizationId,
          staff_id: staffId,
          employee_id: rawEmpId,
          assignment_date: date.format('YYYY-MM-DD'),
          role,
          procedure_name: procName || null,
          department_name: deptName || null,
          pairing_mode: pairingMode,
          paired_with_staff_id: pairedStaffId || null,
          paired_with_employee_id: rawPairedEmp || null,
          paired_with_name: pairedName || null,
          level_at_assignment: lvlAt || null,
          level_after_assignment: lvlAfter || null,
          note: noteVal || null,
        });
      });

      // バッチ挿入
      let successCount = 0;
      const BATCH = 100;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const { error } = await supabase.from('staff_assignments').insert(batch);
        if (error) {
          errors.push(`バッチ挿入エラー: ${error.message}`);
        } else {
          successCount += batch.length;
        }
      }

      setResult({ success: successCount, errors, total: allData.length });
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'staff_assignments',
        targetSummary: `配置履歴 CSV インポート: 成功 ${successCount} / 失敗 ${errors.length} / 全 ${allData.length} 行`,
        changes: { successCount, errorCount: errors.length, total: allData.length },
        success: successCount > 0,
        errorMessage: errors.length ? errors.slice(0, 2).join(' / ') : null,
      });
    } catch (e) {
      setResult({ success: 0, errors: [e.message], total: 0 });
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'staff_assignments',
        targetSummary: `配置履歴 CSV インポートで例外`,
        success: false,
        errorMessage: e.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = [
      'date,employee_id,role,procedure_name,department_name,pairing_mode,paired_with_employee_id,level_at_assignment,level_after_assignment,note',
      '2026-02-10,2100001,scrub,穿頭術,脳神経外科,指導,2100002,指導可,指導可,',
      '2026-02-10,2100002,scrub,穿頭術,脳神経外科,被指導,2100001,見学済み,経験1回,初回経験',
      '2026-02-11,2100001,circulating,胆嚢摘出術,外科,独立,,独り立ち,独り立ち,',
    ].join('\n') + '\n';
    const bom = '\uFEFF';
    const b = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = '配置履歴_テンプレート.csv';
    a.click();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>

        <Link
          to="/surgery-request"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.875rem', textDecoration: 'none', marginBottom: '1.5rem' }}
        >
          ← 手術申込・配置作成へ戻る
        </Link>

        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>
            📋 配置履歴 CSVインポート
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.4rem', fontSize: '0.9rem' }}>
            スタッフの過去配置データをCSVファイルから一括登録できます
          </p>
        </div>

        {/* CSVフォーマット説明 */}
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <p style={{ fontWeight: '600', color: '#1d4ed8', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
            📄 CSVフォーマット
          </p>
          <p style={{ color: '#3b82f6', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            以下のカラムを含むCSVファイルをアップロードしてください。
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
            {[
              ['日付 *', 'date / 日付 / 配置日', '例: 2026-02-10'],
              ['職員番号 *', 'employee_id / 職員番号', '例: 2100001'],
              ['役割 *', 'role / 役割', '例: scrub or circulating\n（器械出し / 外回り）'],
              ['術式名', 'procedure / 術式', '例: 穿頭術'],
              ['診療科', 'department / 診療科', '例: 脳神経外科'],
              ['指導役割', 'pairing_mode / 指導役割', '例: 指導 / 被指導 / 独立\n(instructor/trainee/solo)'],
              ['ペア相手番号', 'paired_with_employee_id', '例: 2100002'],
              ['配置時スキル', 'level_at_assignment', '例: 見学済み'],
              ['配置後スキル', 'level_after_assignment', '例: 経験1回'],
              ['メモ', 'note / 備考', '例: 初回経験'],
            ].map(([label, hint, ex]) => (
              <div key={label} style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #dbeafe', padding: '0.75rem' }}>
                <div style={{ fontWeight: '600', color: '#1d4ed8', fontSize: '0.85rem' }}>{label}</div>
                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.2rem' }}>{hint}</div>
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.2rem', whiteSpace: 'pre-line' }}>{ex}</div>
              </div>
            ))}
          </div>
          <button
            onClick={handleDownloadTemplate}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2563eb', fontSize: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <Download size={14} />
            テンプレートをダウンロード
          </button>

          {/* 注意書き */}
          <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '0.8rem', color: '#92400e', lineHeight: '1.6' }}>
            ⚠️ <strong>ファイルの保存方法について</strong><br />
            <span style={{ display: 'block', marginTop: '0.25rem' }}>
              <strong>Windows（Excel）：</strong>「名前を付けて保存」→「CSV UTF-8（コンマ区切り）」を選択して保存してください。<br />
              <strong>Mac（Numbers）：</strong>編集後、「ファイル → 書き出す → CSV」でCSV形式で書き出してからアップロードしてください。<br />
              通常のCSV形式で保存すると文字化けが発生する場合があります。
            </span>
          </div>
        </div>

        {/* ファイル選択 */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <p style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>CSVファイルを選択</p>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
          <button
            onClick={() => fileInputRef.current.click()}
            style={{
              width: '100%', border: '2px dashed #d1d5db', borderRadius: '10px',
              padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '0.5rem', background: 'none', cursor: 'pointer', color: '#9ca3af',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#14b8a6'; e.currentTarget.style.color = '#0d9488'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#9ca3af'; }}
          >
            <Upload size={32} />
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
              {fileName || 'クリックしてCSVファイルを選択'}
            </span>
          </button>
        </div>

        {/* プレビュー */}
        {preview.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <p style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>プレビュー（先頭5行）</p>
            <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>{headers.map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                      {headers.map(h => (
                        <td key={h} style={{ padding: '0.4rem 0.75rem', color: '#374151' }}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* カラムマッピング */}
        {headers.length > 0 && (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <p style={{ fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>カラムマッピング</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                ['date', '日付 *'],
                ['employee_id', '職員番号 *'],
                ['role', '役割 *'],
                ['procedure_name', '術式名'],
                ['department_name', '診療科'],
                ['pairing_mode', '指導役割'],
                ['paired_with_employee_id', 'ペア相手 職員番号'],
                ['level_at_assignment', '配置時スキル'],
                ['level_after_assignment', '配置後スキル'],
                ['note', 'メモ'],
              ].map(([key, label]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '500', color: '#4b5563', marginBottom: '0.3rem' }}>{label}</label>
                  <select
                    value={mapping[key]}
                    onChange={e => setMapping(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{ width: '100%', fontSize: '0.875rem', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.4rem 0.6rem' }}
                  >
                    <option value="">-- 選択 --</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 結果 */}
        {result && (
          <div style={{
            borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem',
            backgroundColor: result.success > 0 ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${result.success > 0 ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {result.success > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#15803d', marginBottom: '0.5rem' }}>
                <CheckCircle size={18} />
                <span style={{ fontWeight: '600' }}>{result.success}件を登録しました（全{result.total}行）</span>
              </div>
            )}
            {result.errors.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#dc2626', marginBottom: '0.4rem' }}>
                  <AlertCircle size={18} />
                  <span style={{ fontWeight: '600' }}>{result.errors.length}件のエラー</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#ef4444', maxHeight: '120px', overflowY: 'auto' }}>
                  {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* インポートボタン */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <Link
            to="/surgery-request"
            style={{ padding: '0.6rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', color: '#374151', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            キャンセル
          </Link>
          <button
            onClick={handleImport}
            disabled={!headers.length || importing}
            style={{
              padding: '0.6rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600',
              color: 'white', border: 'none', cursor: headers.length && !importing ? 'pointer' : 'not-allowed',
              backgroundColor: headers.length && !importing ? '#0d9488' : '#9ca3af',
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            }}
          >
            {importing ? (
              <><RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />インポート中...</>
            ) : (
              <><Upload size={16} />インポート実行</>
            )}
          </button>
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default StaffAssignmentImport;